import React, { useState } from 'react';
import { Badge, Button, notification, Radio, Spin, Tooltip, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import { ErrorRender } from '@partsbadger/library';
import QuoteStore from '../../stores/QuoteStore';
import { MultipleQuantitiesPrices } from '../MultipleQuantitiesPrices';
import { SummaryVariantGroup } from './summaryGrouping';

interface Props {
    group: SummaryVariantGroup;
    variantIndex: number;
    isAutoQuote: boolean;
    /** masterProductId groups variants; lineItemId targets the specific variant row in the summary. */
    onEditPartSpecifications?: (masterProductId: number, lineItemId: number) => void;
}

const { Text } = Typography;

export const CartItem = ({ group, variantIndex, isAutoQuote, onEditPartSpecifications }: Props) => {
    const { baseItem, items } = group;
    const [loadingItemId, setLoadingItemId] = useState<number | null>(null);
    const [removingVariant, setRemovingVariant] = useState(false);
    const product = baseItem.product;
    const { material, finish, coating } = baseItem;

    const handleRemoveVariant = async () => {
        setRemovingVariant(true);
        await QuoteStore.deleteLineItems(items.map(item => item.uid)).finally(() => setRemovingVariant(false));
    };

    const handleQuoteChange = async (itemId: number, quoteActionValue: string) => {
        setLoadingItemId(itemId);
        const payload = { id: itemId, quote_action: quoteActionValue };
        await QuoteStore.updateLineItem(payload)
            .catch(error => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                });
            })
            .finally(() => setLoadingItemId(null));
    };

    const isEligibleForAutoQuote = (item: SummaryVariantGroup['items'][number]) =>
        item?.safe_auto_quote && !item?.needs_manual_quote;

    const isCustomer = !QuoteStore.for_staff;
    const shouldShowPurchaseModeRadios = !isAutoQuote && isCustomer;

    const masterProductId = product?.master_product;
    const showEditPart = Boolean(onEditPartSpecifications && masterProductId != null);
    const allSafeAutoQuote = items.every(item => item.safe_auto_quote && !item.needs_manual_quote);

    return (
        <div
            className="otf-checkout-variant-card"
            data-line-item-id={baseItem.id}
            data-safe-auto-quote={allSafeAutoQuote ? 'true' : 'false'}
            data-testid="quote-summary-variant"
            data-variant-index={variantIndex}
        >
            <div className="otf-checkout-variant-header">
                <div className="flex items-center">
                    {allSafeAutoQuote ? <Badge color="#52c41a" /> : null}
                    <Text className="otf-checkout-variant-name">{`Variant #${variantIndex}`}</Text>
                </div>

                <div className="otf-checkout-item-specifications">
                    <div className="otf-checkout-item-specification">
                        <span className="otf-checkout-item-specification-label">Material</span>
                        <div className="otf-checkout-item-specification-value">
                            {material ? (
                                <span>
                                    {material.name} {material.name == 'Custom' && product.custom_material}
                                </span>
                            ) : (
                                <span className="text-muted">Not Selected</span>
                            )}
                        </div>
                    </div>
                    <div className="otf-checkout-item-specification">
                        <span className="otf-checkout-item-specification-label">Finish</span>
                        <div className="otf-checkout-item-specification-value">
                            {finish ? (
                                <span>
                                    {finish.name} {finish.name == 'Custom' && product.custom_finish}
                                </span>
                            ) : (
                                <span className="text-muted">Not Selected</span>
                            )}
                        </div>
                    </div>
                    <div className="otf-checkout-item-specification">
                        <span className="otf-checkout-item-specification-label">Coating</span>
                        <div className="otf-checkout-item-specification-value">
                            {coating ? (
                                <span>
                                    {coating.name} {coating.name == 'Custom' && product.custom_coating}
                                </span>
                            ) : (
                                <span className="text-muted">Not Selected</span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="otf-checkout-variant-actions">
                    {showEditPart ? (
                        <Button
                            className="otf-checkout-edit-specifications"
                            onClick={() => onEditPartSpecifications?.(Number(masterProductId), Number(baseItem.id))}
                            size="small"
                            type="primary"
                        >
                            Edit
                        </Button>
                    ) : null}
                    <Tooltip placement="top" title="Remove Variation">
                        <Button
                            aria-label="Remove Variation"
                            className="remove-variant-btn"
                            data-line-item-id={baseItem.id}
                            data-testid="remove-variation"
                            icon={<CloseOutlined />}
                            loading={removingVariant}
                            onClick={handleRemoveVariant}
                            size="small"
                            title="Remove Variation"
                            type="link"
                        />
                    </Tooltip>
                </div>
            </div>

            {shouldShowPurchaseModeRadios ? (
                <div className="otf-checkout-purchase-mode-list">
                    {items.map(item => (
                        <Spin key={item.id} spinning={loadingItemId === item.id} size="small">
                            <div className="otf-checkout-purchase-mode-row">
                                <Text type="secondary" className="otf-checkout-purchase-mode-qty">
                                    {item.quantity} {item.quantity === 1 ? 'unit' : 'units'}
                                </Text>
                                <Radio.Group
                                    value={item.quote_action}
                                    onChange={e => {
                                        if (isEligibleForAutoQuote(item)) {
                                            handleQuoteChange(item.id, e.target.value);
                                        }
                                    }}
                                >
                                    <Radio value={'buy_now'} disabled={!isEligibleForAutoQuote(item)}>
                                        Buy Now
                                    </Radio>
                                    <Radio value={'manual_quote'} disabled={!isEligibleForAutoQuote(item)}>
                                        Manual Quote
                                    </Radio>
                                </Radio.Group>
                            </div>
                        </Spin>
                    ))}
                </div>
            ) : null}

            <div className="otf-checkout-variant-prices">
                <MultipleQuantitiesPrices items={items} showHeader={false} />
            </div>
        </div>
    );
};
